/**
 * Helpers for DSOK translations JSON: { "en": { keyPath: value, ... }, "sk": { ... } }.
 * Keys can be nested (dot-separated keyPath in tree).
 */

/**
 * Flatten nested lang object into keyPath -> value. Recursive.
 * @param {Object} obj - Nested object for one language
 * @param {string} prefix - Current key path prefix
 * @returns {Object} Flat { keyPath: value }
 */
function flattenLangObject(obj, prefix = "") {
  if (obj == null || typeof obj !== "object") {
    return prefix ? { [prefix]: obj } : {};
  }
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value != null && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(result, flattenLangObject(value, path));
    } else {
      result[path] = value;
    }
  }
  return result;
}

/**
 * Get all key paths that appear in any language (union).
 * @param {Object} translationsJson - { en: {...}, sk: {...}, ... }
 * @returns {string[]} Sorted key paths
 */
export function getAllKeyPaths(translationsJson) {
  const set = new Set();
  if (!translationsJson || typeof translationsJson !== "object") return [];
  for (const langObj of Object.values(translationsJson)) {
    if (langObj && typeof langObj === "object") {
      Object.keys(flattenLangObject(langObj)).forEach((k) => set.add(k));
    }
  }
  return Array.from(set).sort();
}

/**
 * Flatten translations JSON to rows: { keyPath, lang, value }[].
 * @param {Object} translationsJson - { en: {...}, sk: {...}, ... }
 * @returns {{ keyPath: string, lang: string, value: string }[]}
 */
export function flattenTranslationsJson(translationsJson) {
  const rows = [];
  if (!translationsJson || typeof translationsJson !== "object") return rows;
  const keyPaths = getAllKeyPaths(translationsJson);
  const langs = Object.keys(translationsJson).filter(
    (l) =>
      translationsJson[l] != null && typeof translationsJson[l] === "object"
  );
  for (const keyPath of keyPaths) {
    for (const lang of langs) {
      const value = getValueByPath(translationsJson, lang, keyPath);
      rows.push({ keyPath, lang, value: value != null ? String(value) : "" });
    }
  }
  return rows;
}

/**
 * Build tree model from key paths. Each node: { id, label, keyPath, children, isLeaf }.
 * id = keyPath or "group:" + keyPath for folders.
 * @param {string[]} keyPaths - Sorted key paths (e.g. ["easyFood.bar.back", "easyFood.bar.next"])
 * @returns {{ id: string, label: string, keyPath: string, children: Array, isLeaf: boolean }[]}
 */
export function buildTreeFromKeyPaths(keyPaths) {
  const root = { children: [], id: "root", label: "root", keyPath: "", isLeaf: false };
  const nodeByPath = new Map();
  nodeByPath.set("", root);

  for (const keyPath of keyPaths) {
    const parts = keyPath.split(".");
    let currentPath = "";
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const prevPath = currentPath;
      currentPath = currentPath ? `${currentPath}.${part}` : part;
      if (nodeByPath.has(currentPath)) continue;
      const isLeaf = i === parts.length - 1;
      const node = {
        id: isLeaf ? currentPath : `group:${currentPath}`,
        label: part,
        keyPath: currentPath,
        children: [],
        isLeaf,
      };
      nodeByPath.set(currentPath, node);
      const parent = nodeByPath.get(prevPath);
      if (parent) parent.children.push(node);
    }
  }
  return root.children;
}

/**
 * Get value at keyPath in lang object. keyPath is dot-separated.
 * @param {Object} translationsJson - { en: {...}, sk: {...} }
 * @param {string} lang - Language code
 * @param {string} keyPath - Dot-separated path
 * @returns {*} Value or undefined
 */
export function getValueByPath(translationsJson, lang, keyPath) {
  if (!translationsJson || !keyPath) return undefined;
  const obj = translationsJson[lang];
  if (!obj || typeof obj !== "object") return undefined;
  const parts = keyPath.split(".");
  let current = obj;
  for (const p of parts) {
    if (current == null || typeof current !== "object") break;
    current = current[p];
  }
  if (current !== undefined && current !== null) return current;
  if (Object.prototype.hasOwnProperty.call(obj, keyPath)) return obj[keyPath];
  return undefined;
}

/**
 * Set value at keyPath in lang object immutably. Returns new translationsJson.
 * @param {Object} translationsJson - Current full object
 * @param {string} lang - Language code
 * @param {string} keyPath - Dot-separated path
 * @param {*} newValue - New value (string)
 * @returns {Object} New translationsJson
 */
export function setValueByPath(translationsJson, lang, keyPath, newValue) {
  const next = JSON.parse(JSON.stringify(translationsJson || {}));
  if (!next[lang]) next[lang] = {};
  const obj = next[lang];
  if (Object.prototype.hasOwnProperty.call(obj, keyPath)) {
    obj[keyPath] = newValue;
    return next;
  }
  const parts = keyPath.split(".");
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!current[p] || typeof current[p] !== "object") current[p] = {};
    current = current[p];
  }
  current[parts[parts.length - 1]] = newValue;
  return next;
}

/**
 * Extract placeholders from string, e.g. {maxItems}, {count}.
 * @param {string} str
 * @returns {string[]} Sorted unique placeholder names
 */
export function extractPlaceholders(str) {
  if (typeof str !== "string") return [];
  const set = new Set();
  const re = /\{([^}]+)\}/g;
  let m;
  while ((m = re.exec(str)) !== null) set.add(m[1]);
  return Array.from(set).sort();
}

/**
 * Check if value looks like it contains HTML (e.g. <br>, <span>).
 * @param {string} str
 * @returns {boolean}
 */
export function containsHtml(str) {
  if (typeof str !== "string") return false;
  return /<[a-zA-Z][^>]*>/.test(str) || /<br\s*\/?>/i.test(str);
}

/**
 * Get languages from translations JSON.
 * @param {Object} translationsJson
 * @returns {string[]}
 */
export function getLanguages(translationsJson) {
  if (!translationsJson || typeof translationsJson !== "object") return [];
  return Object.keys(translationsJson).filter(
    (l) =>
      translationsJson[l] != null && typeof translationsJson[l] === "object"
  );
}

/**
 * Count missing translations for a keyPath across languages.
 * @param {Object} translationsJson
 * @param {string} keyPath
 * @param {string[]} langs
 * @returns {number}
 */
export function countMissingForKey(translationsJson, keyPath, langs) {
  if (!langs || !keyPath) return 0;
  return langs.filter((lang) => {
    const v = getValueByPath(translationsJson, lang, keyPath);
    return v === undefined || v === null || String(v).trim() === "";
  }).length;
}

/**
 * Check if any leaf under group keyPath has missing translations.
 * @param {Object} translationsJson
 * @param {string[]} allKeyPaths
 * @param {string} groupKeyPath - e.g. "easyFood.bar"
 * @param {string[]} langs
 * @returns {boolean}
 */
export function groupHasMissing(translationsJson, allKeyPaths, groupKeyPath, langs) {
  const prefix = groupKeyPath ? `${groupKeyPath}.` : "";
  return allKeyPaths.some((kp) => {
    if (kp === groupKeyPath || (prefix && kp.startsWith(prefix))) {
      return countMissingForKey(translationsJson, kp, langs) > 0;
    }
    return false;
  });
}

const REFERENCE_LANG = "en";

/**
 * Validate that for each keyPath, if the reference language (EN) has placeholders like {maxItems},
 * every other language's value for that key contains the same placeholders.
 * @param {Object} translationsJson - { en: {...}, sk: {...}, ... }
 * @returns {{ valid: boolean, errors: Array<{ keyPath: string, lang: string, missing: string[] }> }}
 */
export function validatePlaceholders(translationsJson) {
  const errors = [];
  if (!translationsJson || typeof translationsJson !== "object") return { valid: true, errors };

  const keyPaths = getAllKeyPaths(translationsJson);
  const langs = getLanguages(translationsJson);
  const refLang = langs.includes(REFERENCE_LANG) ? REFERENCE_LANG : langs[0];
  if (!refLang) return { valid: true, errors };

  for (const keyPath of keyPaths) {
    const refValue = getValueByPath(translationsJson, refLang, keyPath);
    const refStr = refValue != null && typeof refValue === "string" ? refValue : "";
    const refPlaceholderNames = extractPlaceholders(refStr);
    if (refPlaceholderNames.length === 0) continue;

    for (const lang of langs) {
      if (lang === refLang) continue;
      const value = getValueByPath(translationsJson, lang, keyPath);
      const valueStr = value != null ? String(value) : "";
      const valuePlaceholderNames = extractPlaceholders(valueStr);
      const missing = refPlaceholderNames.filter((name) => !valuePlaceholderNames.includes(name));
      if (missing.length > 0) {
        errors.push({
          keyPath,
          lang,
          missing: missing.map((name) => `{${name}}`),
        });
      }
    }
  }
  return { valid: errors.length === 0, errors };
}
